import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { ApiError } from "@/core/api/apiTypes";
import { useParams } from "react-router-dom";
import { ClientEdit } from "../components/ClientEdit";
import { useClient, useUpdateClient } from "../hooks/clientQueries";
import type { Client, UpdateClientRequest } from "../types/clientTypes";

export function ClientEditPage() {
  const { clientId } = useParams<{ clientId: string }>();
  const { goToClients, goToClientDetails } = useAppNavigation();

  const { data: client, isLoading, error, refetch } = useClient(clientId || "");
  const updateClientMutation = useUpdateClient(clientId || "");

  const handleSubmit = (data: Partial<Client>) => {
    const updateClientRequest: UpdateClientRequest = {
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phoneNumber: data.phoneNumber,
      notes: data.notes,
    };

    updateClientMutation.mutate(updateClientRequest, {
      onSuccess: (savedClient) => {
        goToClientDetails(savedClient.id, "finish");
      },
    });
  };

  if (isLoading || !client) {
    const notFound = error instanceof ApiError && error.Status === 404;
    return (
      <Page>
        <PageHeader title="Edit customer" />
        <PageContent>
          {isLoading ? <RowSkeleton /> : error && !notFound ? <ErrorState message={error.message} onRetry={() => void refetch()} /> : <EmptyState message="Customer not found" action={{ label: "View customers", onClick: goToClients }} />}
        </PageContent>
      </Page>
    );
  }

  return (
    <ClientEdit
      key={client.id}
      client={client}
      onSubmit={handleSubmit}
      isSubmitting={updateClientMutation.isPending}
      error={updateClientMutation.error || error}
      title="Edit customer"
      submitButtonText="Save changes"
    />
  );
}
